import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Toolbar,
  Tooltip,
  Alert,
  Chip,
} from '@mui/material';
import {
  FolderOpen,
  InsertDriveFile,
  Download,
  Delete,
  CreateNewFolder,
  Upload,
  Refresh,
  Home,
  CloudUpload,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import edgeMftService, { type MftFile } from '../services/edgeMftService';

const FileManager: React.FC = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState<MftFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);
  

  const loadFiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await edgeMftService.listFiles(currentPath);
      setFiles(response);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [currentPath]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const navigateToPath = (path: string) => {
    setCurrentPath(path);
  };

  const handleFileClick = (file: MftFile) => {
    if (file.is_dir) {
      const newPath = currentPath === '/' 
        ? `/${file.name}` 
        : `${currentPath}/${file.name}`;
      navigateToPath(newPath);
    }
  };

  const handleDownload = async (file: MftFile) => {
    try {
      const filePath = currentPath === '/' 
        ? `/${file.name}` 
        : `${currentPath}/${file.name}`;
      
      const blob = await edgeMftService.downloadFile(filePath);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccess(`Downloaded ${file.name}`);
    } catch (error: any) {
      setError(error.response?.data?.error || `Failed to download ${file.name}`);
    }
  };

  const handleDelete = async (file: MftFile) => {
    if (!confirm(`Are you sure you want to delete "${file.name}"?`)) {
      return;
    }

    try {
      const filePath = currentPath === '/' 
        ? `/${file.name}` 
        : `${currentPath}/${file.name}`;
      
      await edgeMftService.deleteFile(filePath);
      setSuccess(`Deleted ${file.name}`);
      loadFiles();
    } catch (error: any) {
      setError(error.response?.data?.error || `Failed to delete ${file.name}`);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name');
      return;
    }

    try {
      const folderPath = currentPath === '/' 
        ? `/${newFolderName}` 
        : `${currentPath}/${newFolderName}`;
      
      await edgeMftService.createDirectory(folderPath);
      setSuccess(`Created folder ${newFolderName}`);
      setCreateFolderOpen(false);
      setNewFolderName('');
      loadFiles();
    } catch (error: any) {
      setError(error.response?.data?.error || `Failed to create folder ${newFolderName}`);
    }
  };

  const handleFileUpload = async (uploadFiles: File[]) => {
    if (uploadFiles.length === 0) return;

    setUploading(true);
    try {
      for (const file of uploadFiles) {
        await edgeMftService.uploadFile(file, currentPath);
      }
      setSuccess(`Uploaded ${uploadFiles.length} file(s)`);
      loadFiles();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileUpload,
    noClick: false,
    accept: {
      '*/*': []
    }
  });

  const breadcrumbParts = currentPath.split('/').filter(part => part !== '');

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Clear success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        File Transfer
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Upload, download, and manage your files through MFT Edge
      </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}


        {/* Navigation Toolbar */}
        <Paper sx={{ mb: 2, p: 2 }}>
          <Toolbar disableGutters>
            <Breadcrumbs sx={{ flexGrow: 1 }}>
              <Link
                component="button"
                variant="body1"
                onClick={() => navigateToPath('/')}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Home fontSize="small" />
                Root
              </Link>
              {breadcrumbParts.map((part, index) => {
                const path = '/' + breadcrumbParts.slice(0, index + 1).join('/');
                return (
                  <Link
                    key={path}
                    component="button"
                    variant="body1"
                    onClick={() => navigateToPath(path)}
                  >
                    {part}
                  </Link>
                );
              })}
            </Breadcrumbs>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={loadFiles}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Create Folder">
                <IconButton onClick={() => setCreateFolderOpen(true)}>
                  <CreateNewFolder />
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                startIcon={<Upload />}
                component="label"
              >
                Upload
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={(e) => handleFileUpload(Array.from(e.target.files || []))}
                />
              </Button>
            </Box>
          </Toolbar>
        </Paper>

        {/* Drag & Drop Zone */}
        <Paper 
          {...getRootProps()}
          sx={{ 
            mb: 2, 
            p: 3,
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            bgcolor: isDragActive ? 'primary.50' : 'grey.50',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            }
          }}
        >
          <input {...getInputProps()} />
          <Box sx={{ py: 2 }}>
            <CloudUpload 
              sx={{ 
                fontSize: 48, 
                color: isDragActive ? 'primary.main' : 'grey.400',
                mb: 2 
              }} 
            />
            <Typography 
              variant="h6" 
              color={isDragActive ? 'primary.main' : 'text.secondary'}
              gutterBottom
            >
              {isDragActive ? 'Drop files here!' : 'Drag & drop files here'}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
            >
              Or click here to select files
            </Typography>
          </Box>
        </Paper>

        {/* File list */}
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : files.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No files in your transfer area
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Use the drag & drop zone or upload button to transfer files
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow 
                    key={file.name} 
                    hover
                    onClick={() => handleFileClick(file)}
                    sx={{ cursor: file.is_dir ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {file.is_dir ? (
                          <FolderOpen color="primary" />
                        ) : (
                          <InsertDriveFile color="action" />
                        )}
                        <Typography>{file.name}</Typography>
                        {file.is_dir && (
                          <Chip label="DIR" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={file.is_dir ? 'Folder' : 'File'}
                        size="small"
                        color={file.is_dir ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {file.is_dir ? '-' : formatFileSize(file.size)}
                    </TableCell>
                    <TableCell align="right">
                      {!file.is_dir && (
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(file);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(file);
                          }}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Upload progress */}
        {uploading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>Uploading files...</Typography>
          </Box>
        )}

        {/* Create Folder Dialog */}
        <Dialog open={createFolderOpen} onClose={() => setCreateFolderOpen(false)}>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Folder Name"
              fullWidth
              variant="outlined"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreateFolder();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateFolderOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
};

export default FileManager;